import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  MutableRefObject,
  Dispatch,
  SetStateAction,
} from 'react';

import { GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';

import { KasePropertyApi } from '@/api';
import { useKaseContext } from '@/hooks';
import { IProperty } from '@/models';
import { hasAnyOfTargetRoles, isAccountAdmin, isCFSViewOnly } from '@/services';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';

import {
  ICustodyAction,
  KasePropertyEditors,
  KasePropertyViewers,
} from './data';
import { PropertySourceAdapter } from './propertySource';

interface PropertyContextProviderProps extends PropsWithChildren {
  nextRoutes: string[];
  sourceAdapter: PropertySourceAdapter;
}

type CustodyModalValues = {
  open?: boolean;
  propertyOwnerName?: string;
  custodyAction?: ICustodyAction;
  bulkSelection?: string[];
  bulkSelectionItems?: IProperty[];
};

interface PropertyContextValues
  extends Omit<PropertyContextProviderProps, 'children'> {
  parentId: string;
  routeBase: string;
  shouldRefreshProperties: boolean;
  setShouldRefreshProperties: (shouldRefresh: boolean) => void;
  currentPropertyId?: string;
  setCurrentPropertyId: (newPropertyId?: string) => void;
  custodyModalValues: CustodyModalValues;
  setCustodyModalValues: (values: CustodyModalValues) => void;
  bulkSelection: Array<string>;
  bulkSelectionItems: Array<IProperty>;
  setBulkSelection: (id: string[]) => void;
  setBulkSelectionItems: (item: IProperty[]) => void;
  handleBulkSelection: (id?: string) => void;
  handleBulkSelectionItem: (item?: IProperty) => void;
  handleCloseCustodyModal: () => void;
  resetBulkSelections: () => void;
  isKasePropertyEditor?: boolean;
  isKasePropertyViewer?: boolean;
  isReadOnly: boolean;
  apiRef?: MutableRefObject<GridApiPro>;
  refreshKey?: string;
  setRefreshKey?: Dispatch<SetStateAction<string>>;
  agencyPropertyTypes: { value: string; label: string }[];
  setAgencyPropertyTypes: Dispatch<
    SetStateAction<{ value: string; label: string }[]>
  >;
}

const PropertyContext = createContext<PropertyContextValues | undefined>(
  undefined,
);

export function usePropertyContext() {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error(
      'usePropertyContext must be used within a PropertyContextProvider',
    );
  }
  return context;
}

export function PropertyContextProvider({
  children,
  ...rest
}: PropertyContextProviderProps) {
  const { user } = useUserStore();
  const { updateToast } = useToastStore();
  const { isReadOnlyProperties } = useKaseContext();

  const [custodyModalValues, setCustodyModalValues] =
    useState<CustodyModalValues>({});
  const [currentPropertyId, setCurrentPropertyId] = useState<string>();
  const [bulkSelection, setBulkSelection] = useState<string[]>([]); // returns IDs of selected Properties
  const [bulkSelectionItems, setBulkSelectionItems] = useState<IProperty[]>([]); // returns selected Properties
  const [shouldRefreshProperties, setShouldRefreshProperties] = useState(false);
  const [refreshKey, setRefreshKey] = useState<string>('');
  const [agencyPropertyTypes, setAgencyPropertyTypes] = useState<
    { value: string; label: string }[]
  >([]);

  const agencyId = useMemo(
    () => localStorage.getItem('current-agency-id'),
    [user],
  );

  // CFS (View Only) users have read-only access; for the CFS source this drives
  // hiding of every modify affordance in the shared properties feature.
  const isCfsViewOnly =
    rest.sourceAdapter.source === 'cfs' && isCFSViewOnly(user);
  const isReadOnly = isCfsViewOnly || isReadOnlyProperties;

  useEffect(() => {
    // Skip the kase property-types metadata fetch for CFS view-only users — it
    // 403s (no kase access) and is only needed for the edit type dropdown.
    if (agencyId && !isCfsViewOnly) {
      KasePropertyApi.getPropertyTypes()
        .then((res) => {
          if (res?.data?.length) {
            setAgencyPropertyTypes(
              res.data.map((item) => ({
                value: item._id,
                label: item.property_name,
              })),
            );
          }
        })
        .catch((e) => {
          updateToast({ open: true, message: e.message });
        });
    }
  }, [agencyId]);

  const apiRef = useGridApiRef();

  const handleBulkSelectionItem = (item?: IProperty) => {
    if (!item) {
      return;
    }

    if (bulkSelectionItems?.length === 0) {
      setBulkSelectionItems((state) => [...state, item]);
      return;
    }

    const selectedStatuses = bulkSelectionItems.map(
      (selectedItem) => selectedItem.status,
    );
    const statusMatches = selectedStatuses.includes(item.status);

    // If the status doesn't match any already selected status, don't add the item
    if (!statusMatches) {
      updateToast({
        type: ConfirmTypes.WARNING,
        open: true,
        message: 'Only properties with same statuses can be selected',
      });
      return;
    }

    // If the item is already in the selection, remove it
    const itemIndex = bulkSelectionItems.findIndex(
      (selectedItem) => selectedItem._id === item._id,
    );

    if (itemIndex !== -1) {
      setBulkSelectionItems((state) =>
        state.filter((selectedItem) => selectedItem._id !== item._id),
      );
    } else {
      setBulkSelectionItems((state) => [...state, item]);
    }
  };

  const handleBulkSelection = (id?: string) => {
    if (!id) {
      return;
    }
    if (bulkSelection.includes(id)) {
      setBulkSelection((state) => state.filter((itemID) => itemID !== id));
    } else {
      setBulkSelection((state) => [...state, id]);
    }
  };

  const handleCloseCustodyModal = () => {
    setCustodyModalValues({});
    setRefreshKey((prevState) => prevState + 1);
  };

  const resetBulkSelections = () => {
    setBulkSelection([]);
    setBulkSelectionItems([]);
    apiRef?.current.setRowSelectionModel([]);
  };

  const [isKasePropertyEditor, isKasePropertyViewer] = useMemo(() => {
    if (!user || !agencyId) return [false, false];
    const isEditor =
      isAccountAdmin(user) ||
      hasAnyOfTargetRoles(user, KasePropertyEditors, agencyId);
    if (isEditor) return [isEditor, isEditor];
    const isViewer =
      hasAnyOfTargetRoles(user, KasePropertyViewers, agencyId) || isCfsViewOnly;
    return [isEditor, isViewer];
  }, [user, agencyId, isCfsViewOnly]);

  return (
    <PropertyContext.Provider
      value={{
        parentId: rest.sourceAdapter.parentId,
        routeBase: rest.sourceAdapter.routeBase,
        currentPropertyId,
        setCurrentPropertyId,
        shouldRefreshProperties,
        setShouldRefreshProperties,
        custodyModalValues,
        setCustodyModalValues,
        bulkSelection,
        bulkSelectionItems,
        setBulkSelection,
        setBulkSelectionItems,
        handleBulkSelection,
        handleBulkSelectionItem,
        handleCloseCustodyModal,
        resetBulkSelections,
        isKasePropertyEditor,
        isKasePropertyViewer,
        isReadOnly,
        apiRef,
        refreshKey,
        setRefreshKey,
        agencyPropertyTypes,
        setAgencyPropertyTypes,
        ...rest,
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
}

export const useKasePropertyContext = usePropertyContext;
export const KasePropertyContextProvider = PropertyContextProvider;
